import { RawSkillAssessmentDto, SkillDto, SkillTypeDto } from '@/api/generated';
import { useMobileMediaQuery } from '@/utils/useMobileMediaQuery.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import React from 'react';

import SkillRatingItem from './SkillRatingItem';
import SkillSectionHeader from './SkillSectionHeader';
import { RatingScale } from './utils/ratingScales';

interface SkillSectionProps {
  disabled: boolean;
  onChange: (
    skillName: string,
    skillId: number,
  ) => (event: Event, value: number[] | number) => void;
  scale: RatingScale;
  skillAssessment: RawSkillAssessmentDto[];
  skills: SkillDto[];
  type: SkillTypeDto;
}

const SkillSection: React.FC<SkillSectionProps> = ({
  disabled,
  onChange,
  scale,
  skillAssessment,
  skills,
  type,
}) => {
  const { t } = useTranslationWithPrefix('skillsAssessmentCollectedPage');
  const isMobile = useMobileMediaQuery();
  const isLanguageSection = type === SkillTypeDto.Language;

  return (
    <section
      className="rounded-xl mb-5 md:mb-6 w-full overflow-hidden shadow-sm transition-shadow hover:shadow-md"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e8e8e8',
      }}
    >
      <div
        className="flex items-center gap-3 px-4 lg:px-6 py-4"
        style={{
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <h2
          className="text-base md:text-lg font-bold m-0"
          style={{ color: '#f16e00', letterSpacing: '-0.01em' }}
        >
          {t(`skillTypes.${type}`)}
        </h2>
      </div>

      {isLanguageSection && (
        <div
          className="px-4 lg:px-6 py-3"
          style={{
            backgroundColor: 'rgba(241, 110, 0, 0.03)',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <p className="m-0 text-sm" style={{ color: '#666' }}>
            {t('languageIntroText')}
          </p>
        </div>
      )}

      <SkillSectionHeader
        isLanguageSection={isLanguageSection}
        isMobile={isMobile}
        labels={scale.labels}
      />

      <div>
        {skills.map((skill, index) => {
          const value = skillAssessment.find(s => s.skillId === skill.skillId)?.level ?? scale.min;
          const isLast = index === skills.length - 1;

          return (
            <div
              key={skill.skillId}
              style={{
                borderBottom: isLast ? 'none' : undefined,
              }}
            >
              <SkillRatingItem
                disabled={disabled}
                onChange={onChange(skill.name, skill.skillId)}
                scale={scale}
                skill={skill}
                value={value}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SkillSection;
